/**
 * Barra lateral del backoffice.
 *
 * Los enlaces van agrupados por lo que la persona viene a hacer (operar,
 * mantener el catalogo, revisar el sistema) y no por como esta organizado el
 * codigo. `NavLink` marca la seccion activa con `aria-current`, que es lo que
 * un lector de pantalla anuncia como "pagina actual".
 *
 * Abajo cierra con quien esta conectado, el cambio de tema y la salida: en un
 * panel que comparten hasta diez personas, saber con que cuenta se esta
 * trabajando importa antes de editar un precio.
 *
 * El boton de tema es el MISMO componente que usa la cabecera de la tienda, con
 * otra clase: el tema es uno solo y vive en el `data-theme` del documento, asi
 * que cambiarlo desde el backoffice tambien cambia la tienda.
 *
 * Salir pide confirmacion. El boton esta a un pixel del de tema y del pie de la
 * barra, en una pantalla donde se trabaja con formularios abiertos: un clic mal
 * puesto cierra la sesion y bota lo que se estaba escribiendo. El dialogo queda
 * fuera del `<aside>` a proposito: no es parte de la navegacion.
 *
 * En el telefono la barra se pliega: queda el logo y el boton de tres lineas, y
 * al tocarlo la navegacion y la sesion se despliegan hacia abajo a todo el
 * ancho. Elegir una seccion la vuelve a plegar.
 */
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import { SITIO } from '../../configuracion/sitio';
import { useSesion } from '../../contexto/sesion-contexto';
import { rutas } from '../../utilidades/rutas';
import { BotonTema } from '../diseno/BotonTema';
import { Icono, type NombreIcono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { Modal } from './Modal';

interface Enlace {
  ruta: string;
  icono: NombreIcono;
  texto: string;
  /** Solo para la ruta raiz del backoffice, que si no queda siempre activa. */
  exacta?: boolean;
}

interface Grupo {
  titulo: string;
  enlaces: Enlace[];
}

const GRUPOS: Grupo[] = [
  {
    titulo: 'Operación',
    enlaces: [
      { ruta: rutas.administracion.panel(), icono: 'panel', texto: 'Panel', exacta: true },
      { ruta: rutas.administracion.pedidos(), icono: 'caja', texto: 'Pedidos' },
      { ruta: rutas.administracion.despacho(), icono: 'camion', texto: 'Despacho' },
      { ruta: rutas.administracion.avisosVenta(), icono: 'correo', texto: 'Avisos de compra' },
      {
        ruta: rutas.administracion.busquedas(),
        icono: 'lupaVacia',
        texto: 'Búsquedas sin resultado',
      },
    ],
  },
  {
    titulo: 'Catálogo',
    enlaces: [
      { ruta: rutas.administracion.productos(), icono: 'lista', texto: 'Productos' },
      { ruta: rutas.administracion.portada(), icono: 'rayo', texto: 'Portada' },
    ],
  },
  {
    titulo: 'Sistema',
    enlaces: [
      { ruta: rutas.administracion.random(), icono: 'sincro', texto: 'Integración Random' },
      {
        ruta: rutas.administracion.administradores(),
        icono: 'personas',
        texto: 'Administradores',
      },
    ],
  },
];

export function NavegacionAdmin() {
  const { administrador, salir } = useSesion();
  const navegar = useNavigate();
  const [confirmando, setConfirmando] = useState(false);
  const [desplegado, setDesplegado] = useState(false);

  function plegar(): void {
    setDesplegado(false);
  }

  function cerrarSesion(): void {
    setConfirmando(false);
    salir();
    navegar(rutas.administracion.acceso(), { replace: true });
  }

  return (
    <>
      <aside className={desplegado ? 'admin-lateral desplegado' : 'admin-lateral'}>
        <div className="admin-marca">
          <Imagen src={SITIO.logo} alt={SITIO.nombreLargo} ancho={68} alto={34} prioridad />
          <span className="txt">
            <span className="t2">Backoffice</span>
          </span>
        </div>

        <button
          type="button"
          className="admin-accion admin-menu-boton"
          aria-expanded={desplegado}
          aria-controls="admin-desplegable"
          aria-label={desplegado ? 'Cerrar el menú' : 'Abrir el menú'}
          onClick={() => {
            setDesplegado((abierto) => !abierto);
          }}
        >
          <Icono nombre={desplegado ? 'cerrar' : 'menu'} />
        </button>

        <div className="admin-desplegable" id="admin-desplegable">
          <nav className="admin-nav" aria-label="Secciones del backoffice">
            {GRUPOS.map((grupo) => (
              <div key={grupo.titulo}>
                <p className="grupo">{grupo.titulo}</p>
                {grupo.enlaces.map((enlace) => (
                  <NavLink
                    key={enlace.ruta}
                    to={enlace.ruta}
                    end={enlace.exacta ?? false}
                    className={({ isActive }) => (isActive ? 'admin-link activo' : 'admin-link')}
                    onClick={plegar}
                  >
                    <Icono nombre={enlace.icono} />
                    <span>{enlace.texto}</span>
                  </NavLink>
                ))}
              </div>
            ))}

            <p className="grupo">Tienda</p>
            <NavLink to={rutas.inicio()} className="admin-link" onClick={plegar}>
              <Icono nombre="tienda" />
              <span>Ver la tienda</span>
            </NavLink>
          </nav>

          {administrador !== null ? (
            <div className="admin-sesion">
              <span className="quien">
                <b>{administrador.nombre}</b>
                <span>{administrador.email}</span>
              </span>
              <BotonTema className="admin-accion" />
              <button
                type="button"
                className="admin-accion"
                onClick={() => {
                  setConfirmando(true);
                }}
                aria-label="Salir del panel"
                title="Salir del panel"
              >
                <Icono nombre="salir" />
              </button>
            </div>
          ) : null}
        </div>
      </aside>

      <Modal
        titulo="Salir del panel"
        abierto={confirmando}
        onCerrar={() => {
          setConfirmando(false);
        }}
      >
        <p className="parrafo-panel">
          Se va a cerrar la sesión de <strong>{administrador?.nombre}</strong> en este equipo.
          Para volver a entrar hay que escribir la clave de nuevo.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            onClick={() => {
              setConfirmando(false);
            }}
          >
            Cancelar
          </button>
          <button type="button" className="btn btn-primario" onClick={cerrarSesion}>
            Salir
          </button>
        </div>
      </Modal>
    </>
  );
}
